import {
  useGetDevice,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetTasks,
  useGetUser,
} from "@hooks";
import { AssistantProcessStatus } from "@models";
import { Alert, AlertTitle, IconButton } from "@mui/material";
import { TemplateQuestion } from "@types";
import { ChevronLeft, ChevronRight, SquareArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { ProgressBubbleLine } from "src/components/Form/ProgressBubbleLine";
import { AssistantMultiStepFormStep } from "src/components/FormStep/AssistantMultiStepFormStep";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import { useUpdateAssistantProcessMutation } from "src/hooks/queries/assistant-process-mutations";
import { Loading } from "src/pages";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { isStepComplete } from "src/utils/step";

/**
 * Collection of user updates in an AssistantProcess.
 * Nothing is created in the MultiStepForm part of the process, just answers collected to be updated and created after.
 */
export const DU_MultiStepForm = () => {
  const { orgId = "", deviceId = "" } = useParams();
  const navigate = useNavigate();

  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
  });

  // const { mutate: updateAssistantProcessDebounced } =
  //   useUpdateAssistantProcessMutationDebounced(orgId, deviceId);

  const { mutate: updateAssistantProcess } = useUpdateAssistantProcessMutation(
    orgId,
    deviceId
  );
  const { data: user } = useGetUser();

  const { data: device } = useGetDevice({
    orgId,
    deviceId,
  });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: tasks } = useGetTasks(orgId, deviceId);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const assistantProcess = assistantProcesses?.[0];

  // Get assistant process info
  const deviceDocumentChangeList =
    assistantProcess?.state.dataKeysToUpdate || [];
  const answers = assistantProcess?.state.changes || [];
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (assistantProcess && !isInitialized) {
      setActiveStepIdx(answers.length > 0 ? answers.length - 1 : 0);
      setIsInitialized(true);
    }
  }, [assistantProcess]);

  const activeTemplateKey: string | undefined = deviceDocumentChangeList?.[
    activeStepIdx
  ]?.templateKey as string;
  const activeTemplateDocName =
    templatesConfig?.[activeTemplateKey]?.defaultTitle;
  const activeTemplateDocType =
    templatesConfig?.[activeTemplateKey]?.regulatoryType;
  const activeDocumentVersion =
    activeTemplateDocType === "RCD"
      ? undefined
      : documents?.find((doc) => doc.templateKey === activeTemplateKey)
        ?.versions[0];
  const activeAnswer = answers[activeStepIdx];
  const steps = deviceDocumentChangeList
    ?.map(({ templateKey, dataKeyId }) =>
      templatesConfig?.[templateKey]?.questions.find((e) => e.id === dataKeyId)
    )
    .filter((step) => !!step);
  const activeTemplateQuestionWithActions: TemplateQuestion | undefined =
    steps[activeStepIdx];

  const isReadyForNextStep = useMemo(
    () =>
      activeTemplateQuestionWithActions && activeAnswer?.acceptedAnswer
        ? isStepComplete(
          activeTemplateQuestionWithActions,
          activeAnswer.acceptedAnswer
        )
        : false,
    [activeTemplateQuestionWithActions, activeAnswer?.acceptedAnswer]
  );

  // ---- App Actions ----

  const endChangeManagement = () => {
    if (!assistantProcess) return;
    updateAssistantProcess({
      processId: assistantProcess.id,
      body: {
        status: AssistantProcessStatus.CANCELLED,
      },
    });
    navigate(
      fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: deviceId,
      })
    );
  };

  const handleGoToNextStep = () => {
    if (activeStepIdx < deviceDocumentChangeList.length - 1) {
      setActiveStepIdx(activeStepIdx + 1);
    } else if (activeStepIdx === deviceDocumentChangeList.length - 1) {
      navigate(
        fillRouteWithSlugs(ROUTES.ASSISTANT_DU_UPDATE_DOCS, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        })
      );
    }
  };

  const handleGoToPreviousStep = () => {
    if (activeStepIdx > 0) {
      setActiveStepIdx(activeStepIdx - 1);
    }
  };

  const isRcd = activeTemplateDocType === "RCD";

  if (
    !documents ||
    !device ||
    !assistantProcess ||
    !user ||
    !templatesConfig ||
    !tasks
  ) {
    return <Loading />;
  }

  const isLastStep =
    activeTemplateQuestionWithActions.id === steps[steps.length - 1].id;
  const isFirstStep = activeTemplateQuestionWithActions.id === steps[0].id;

  const backDisabled = isFirstStep;
  const forwardDisabled = isLastStep || !isReadyForNextStep;

  return (
    <div
      key={activeTemplateQuestionWithActions.id}
      className="flex flex-1 flex-col gap-y-2"
    >
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              { label: "Document Change Assistant" },
              { label: activeTemplateDocName },
            ]}
          />
        }
        leftButtons={
          <IconButton
            onClick={() => setOpenConfirmationModal(true)}
            size="small"
            edge="start"
          >
            <SquareArrowLeft className="text-gray-500 size-[16px]" />
          </IconButton>
        }
        rightButtons={
          <div className="flex gap-x-1">
            <IconButton
              onClick={handleGoToPreviousStep}
              disabled={backDisabled}
              size="small"
            >
              <ChevronLeft className="text-gray-500 size-[16px]" />
            </IconButton>
            <IconButton
              onClick={handleGoToNextStep}
              disabled={forwardDisabled}
              size="small"
            >
              <ChevronRight className="text-gray-500 size-[16px]" />
            </IconButton>
          </div>
        }
      />
      <div className="py-2 px-3">
        <div className={`flex flex-col`}>
          <ProgressBubbleLine
            className="w-[400px]"
            totalItemCount={deviceDocumentChangeList.length}
            currentItemIndex={activeStepIdx}
          />
        </div>
        <div className="flex flex-col">
          {activeTemplateQuestionWithActions &&
            (activeDocumentVersion || isRcd) ? (
            <AssistantMultiStepFormStep
              templatesConfig={templatesConfig}
              user={user}
              templateKey={activeTemplateKey}
              documentVersion={activeDocumentVersion}
              key={activeTemplateQuestionWithActions.id}
              step={activeTemplateQuestionWithActions}
              nextStep={handleGoToNextStep}
              // answer={activeAnswer}
              // handleAnswerChange={handleCurrentAnswerChange}
              documents={documents}
              device={device}
              assistantProcess={assistantProcess}
              //isReadyForNextStep={isReadyForNextStep}
              tasks={tasks}
            />
          ) : (
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              Missing necessary document data. Have you started the document?
            </Alert>
          )}
        </div>
      </div>
      <ConfirmationModal
        title="End Change Management"
        confirmText="Confirm"
        content="Are you sure you want to end change management? All progress made will be erased."
        onAbort={() => setOpenConfirmationModal(false)}
        onConfirm={endChangeManagement}
        open={openConfirmationModal}
      />
    </div>
  );
};
